import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAuthQuery } from "../base/baseApi";
import { ApiResponse } from "../base/apiResponse";
import { EnrollmentPaymentByOperatorRequest, PaymentResponse } from "../../models/dtos";
import { studentsApi } from "../students/studentsApi";

export const paymentsApi = createApi({
  reducerPath: "paymentsApi",
  baseQuery: baseAuthQuery("payments"),
  tagTypes: ["Payment"],
  endpoints: (builder) => ({
    getPaymentFiles: builder.query<ApiResponse<PaymentResponse[]>, void>({
      query: (_query) => {
        return {
          url: "getPaymentFiles",
          method: "GET",
        };
      },
      providesTags: (_result, _error) => {
        return ["Payment"];
      },
    }),
    verifyPaymentFile: builder.mutation<ApiResponse<boolean>, { paymentId: number; paymentApproved: boolean }>({
      query: (body) => {
        return {
          url: `verifyPaymentFile/${body.paymentId}/${body.paymentApproved}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Payment"],
    }),
    updatePaymentAmount: builder.mutation<ApiResponse<boolean>, { paymentId: number; amount: number }>({
      query: (body) => {
        return {
          url: `updatePaymentAmount/${body.paymentId}`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: ["Payment"],
    }),
    getPaymentFile: builder.query<Blob, { paymentId: number }>({
      query: (params) => ({
        url: `getPaymentFile/${params.paymentId}`,
        method: "GET",
        responseHandler: async (response) => {
          const blob = await response.blob();
          const fileName = response.headers.get("Content-Disposition")!.split(";")[1].split("=")[1];
          return { blob, fileName };
        },
      }),
    }),
    addPayment: builder.mutation<ApiResponse<boolean>, { studentId: number; amount: number }>({
      query: (body) => {
        return {
          url: "addPayment",
          method: "POST",
          body: body,
        };
      },
      async onQueryStarted({ studentId, amount: _amount }, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(studentsApi.util.invalidateTags([{ type: "StudentPayments", id: studentId }]));
      },
    }),
    enrollmentPaymentByOperator: builder.mutation<ApiResponse<boolean>, EnrollmentPaymentByOperatorRequest>({
      query: (body) => {
        return {
          url: "enrollmentPaymentByOperator",
          method: "POST",
          body: body,
        };
      },
    }),
    updatePayment: builder.mutation<ApiResponse<boolean>, { paymentId: number; amount: number; startedOn: string }>({
      query: (body) => {
        return {
          url: `update/${body.paymentId}`,
          method: "POST",
          body: {
            amount: body.amount,
            startedOn: body.startedOn,
          },
        };
      },
      invalidatesTags: ["Payment"],
    }),
    deletePayment: builder.mutation<ApiResponse<boolean>, number>({
      query: (paymentId) => {
        return {
          url: `delete/${paymentId}`,
          method: "POST",
        };
      },
      invalidatesTags: ["Payment"],
    }),
    getSemesterFeePaymentsReport: builder.mutation<Blob, { facultyId?: number; startDate?: string; endDate?: string }>({
      query: (body) => ({
        url: "reports/semesterFeePayments",
        method: "POST",
        body: body,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
    }),
  }),
});
